/**
 * CihCoachGrid — contained 3-column grid of CihCoachCell soft rounded cards.
 * Limits visible cells to 6 with "View all" link when more exist.
 *
 * Aliases: coach grid, coach roster, cih coaches
 */

import {
  CihCoachCellGrid,
  type CihCoachCellData,
} from "~/components/cih/coach-cell";
import { CihSectionHeader } from "~/components/cih/section-header";

type Props = {
  readonly coaches: ReadonlyArray<CihCoachCellData>;
  readonly sectionLabel: string;
  readonly heading: string;
  readonly seeAllHref?: string;
  /**
   * Total number of coaches available (across all pages). Defaults to
   * `coaches.length` when the caller is rendering the entire list up front.
   */
  readonly totalCount?: number;
};

const INLINE_LIMIT = 6;

export function CihCoachGrid({
  coaches,
  sectionLabel,
  heading,
  seeAllHref,
  totalCount,
}: Props) {
  if (coaches.length === 0) return null;

  const visible = coaches.slice(0, INLINE_LIMIT);
  const total = totalCount ?? coaches.length;
  const hasMore = total > INLINE_LIMIT;

  return (
    <>
      <CihSectionHeader label={sectionLabel} title={heading} />
      <div className="mx-auto max-w-7xl px-4 pb-14 md:pb-20 xl:px-0">
        <CihCoachCellGrid coaches={visible} />
        {seeAllHref && hasMore ? (
          <div className="mt-6 text-center">
            <a
              href={seeAllHref}
              className="text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              View all coaches →
            </a>
          </div>
        ) : null}
      </div>
    </>
  );
}
